import {EntityHistory} from "./EntityHistory";
import VehicleType from "./enums/VehicleType.js";
import {findEntityById, toFullEntityResult} from "./helpers/toFullEntityResult";
import IHolder from "./interfaces/IHolder";
import {VehicleId} from "./nominalTypes/VehicleId";
import {FullEntityRelation} from "./types/EntityRelation";
import {PermitEntityResult, PermitFullEntityResult} from "./types/PermitEntityResult";
import {VehicleResult} from "./types/VehicleResult";

function createEmptyVehicle(vehicleId: VehicleId): VehicleResult {
	return {
		id: vehicleId,
		plate: vehicleId,
		type: VehicleType.Resident,
		hidden: false,
		attachments: [],
		customData: []
	}
}

function getSingle(holder: IHolder, permitResult: PermitEntityResult<VehicleId>): PermitFullEntityResult<VehicleId, VehicleResult>;
function getSingle(holder: IHolder, permitResult: PermitEntityResult<VehicleId> | undefined): PermitFullEntityResult<VehicleId, VehicleResult> | undefined {
	if (permitResult == null) {
		return undefined;
	}
	return toFullEntityResult(holder.vehicles, permitResult, createEmptyVehicle);
}

class Vehicles {
	static get(holder: IHolder, id: VehicleId): VehicleResult {
		return findEntityById(holder.vehicles, id, createEmptyVehicle);
	}

	/** Whether the holder actually owns this vehicle. `get` never fails - it fabricates a
	 *  placeholder for unknown ids - so callers that must tell them apart use this. */
	static exists(holder: IHolder, id: VehicleId): boolean {
		return holder.vehicles.some(x => x.id === id);
	}

	static getAll(holder: IHolder, vehicleIds: VehicleId[]): VehicleResult[] {
		const result: VehicleResult[] = [];
		for (const id of vehicleIds) {
			result.push(this.get(holder, id));
		}
		return result;
	}

	static getChanged(holder: IHolder, vehicles: PermitEntityResult<VehicleId>[]): FullEntityRelation<VehicleId, VehicleResult>[] {
		const result: FullEntityRelation<VehicleId, VehicleResult>[] = [];
		for (const relation of EntityHistory.allChanged(vehicles)) {
			const original = getSingle(holder, relation.original!);
			const changed = relation.changed && getSingle(holder, relation.changed);

			result.push({
				original: toFullEntityResult(holder.vehicles, original, createEmptyVehicle),
				changed: changed && toFullEntityResult(holder.vehicles, changed, createEmptyVehicle)
			});
		}

		return result;
	}

	static getPlatesByType(holder: IHolder, type: VehicleType): string[] {
		const result: string[] = [];
		for (const x of holder.vehicles) {
			if (x.type === type) {
				result.push(x.plate);
			}
		}
		return result;
	}

	static getValid(holder: IHolder, vehicles: PermitEntityResult<VehicleId>[]): PermitFullEntityResult<VehicleId, VehicleResult>[] {
		const changed = EntityHistory.allChanged(vehicles);
		const result: PermitFullEntityResult<VehicleId, VehicleResult>[] = [];
		for (const e of changed) {
			result.push(getSingle(holder, e.original!));
		}
		return result;
	}
}

export {Vehicles};
